import { Close } from '@mui/icons-material';
import { Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface SearchBarProps {
  filterSearchCard: string;
  onChangeSearchFilterCards: (filterSearchCard: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  filterSearchCard,
  onChangeSearchFilterCards,
}) =>
{
  const [isSearchBarOpened, setIsSearchBarOpened] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const inputSearchBar = useRef<HTMLInputElement>(null);

  useEffect(() =>
  {
    if (isSearchBarOpened)
    {
      inputSearchBar.current?.focus();
    }
  }, [isSearchBarOpened]);

  // When the parent clears the applied search (e.g. on dashboard switch),
  // also reset the input text and close the search bar.
  useEffect(() =>
  {
    if (!filterSearchCard)
    {
      setInputValue('');
      setIsSearchBarOpened(false);
    }
  }, [filterSearchCard]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter' && inputValue.trim())
    {
      onChangeSearchFilterCards(inputValue.trim());
      setIsSearchBarOpened(false);
    }
    else if (e.key === 'Escape')
    {
      setInputValue('');
      setIsSearchBarOpened(false);
    }
  };

  const handleClear = () =>
  {
    setInputValue('');
    onChangeSearchFilterCards('');
    setIsSearchBarOpened(false);
  };

  // Chip mode: search applied
  if (filterSearchCard)
  {
    return (
      <div className="flex items-center gap-1 pr-4">
        <div className="flex items-center gap-1 py-1.5 pl-2 pr-1.5 bg-white dark:bg-[#3a3c3c] border border-gray-400 dark:border-[#4a4c4c] rounded">
          <Search className="w-3.5 h-3.5 text-[#62aacb] flex-shrink-0" />
          <div className="flex items-center gap-0.5 bg-[#f6f6f6] dark:bg-[#4a4c4c] rounded-[3px] px-1.5 leading-none" style={{ lineHeight: 1 }}>
            <span
              className="text-[10px] overflow-hidden text-ellipsis whitespace-nowrap"
              style={{ maxWidth: '120px' }}
            >
              {filterSearchCard}
            </span>
            <button
              onClick={handleClear}
              className="flex-shrink-0 leading-none hover:opacity-70"
              style={{ color: '#414042' }}
            >
              <Close style={{ fontSize: 10 }} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Input mode: typing
  if (isSearchBarOpened)
  {
    return (
      <div className="flex items-center gap-1 pr-4">
        <div className="relative">
          <button
            onClick={() => setIsSearchBarOpened(false)}
            className="absolute top-1/2 left-2 -translate-y-1/2 text-[#62aacb]"
          >
            <Search className="w-3.5 h-3.5" />
          </button>
          <input
            ref={inputSearchBar}
            type="text"
            placeholder="Search cards"
            className="w-60 py-1.5 pl-7 pr-6 text-xs border border-gray-400 dark:border-[#4a4c4c] rounded focus:outline-none focus:border-[#62aacb] bg-white dark:bg-[#3a3c3c] dark:text-[#a9a093] dark:placeholder:text-[#6e6860]"
            onChange={e => setInputValue(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            value={inputValue}
          />
          {inputValue && (
            <button
              onClick={() => setInputValue('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#6e6860] hover:text-gray-600 dark:hover:text-[#a9a093]"
            >
              <Close style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Collapsed mode: just the icon
  return (
    <div className="flex items-center gap-1 pr-4">
      <div className="relative mb-2 pr-2">
        <button
          onClick={() => setIsSearchBarOpened(true)}
          className="absolute top-1 pl-2 -translate-y-1.5 text-gray-400 dark:text-[#b1a99d] hover:text-gray-600 dark:hover:text-[#a9a093]"
        >
          <Search className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
